import { computed, ref } from 'vue'

import { isAxiosError } from 'axios'
import { useToast } from 'primevue/usetoast'

import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'

import router from '@/router'
import { API_URL } from '@/helpers/api-url.helper'
import {
  closeTrackedSipWebSockets,
  setSipWebSocketsBlocked,
} from '@/helpers/sip-websocket-lifecycle.helper'
import { useWebphonePermissions } from '@/composables/useWebphonePermissions'
import { useAuthService } from '@/services/auth/useAuthService'
import { useWorkspaceStore } from './useWorkspaceStore'

const EXTENSION_NOT_FOUND_DETAIL = 'Número de extension no encontrado'

export const useAuthStore = defineStore('auth', () => {
  const { t } = useI18n()
  const authService = useAuthService()
  const workspaceStore = useWorkspaceStore()
  const toast = useToast()
  const { hasRequiredPermissions } = useWebphonePermissions()

  const token = ref<string | null>(localStorage.getItem('tokenHub') ||  null)
  const getStoredExtension = () => {
    const ext = localStorage.getItem('extension')
    if (!ext) return null

    try {
      return JSON.parse(ext)
    } catch {
      return null
    }
  }

  const resolveWebphoneDomain = (extensionData?: any | null) => {
    const fromEnv = (import.meta.env['VITE_WEBPHONE_DOMAIN'] as string | undefined)?.trim()
    if (fromEnv) return fromEnv

    const fromExtension = [
      extensionData?.domain,
      extensionData?.sip_domain,
      extensionData?.sipDomain,
      extensionData?.host,
      extensionData?.server,
      extensionData?.pbx,
    ].find((value) => typeof value === 'string' && value.trim())
    if (fromExtension) return String(fromExtension).trim()

    try {
      return new URL(API_URL).hostname
    } catch {
      return window.location.hostname
    }
  }

  const webphoneNumber = computed(() => extension.value?.number || '')
  const webphonePassword = computed(() => extension.value?.key || '')
  const extension = ref<any | null>(getStoredExtension())
  const webphoneDomain = ref<string>(resolveWebphoneDomain(extension.value))
  const showWebphone = ref(true)
  const webphoneTarget = ref('')
  const webphoneRemoteNumber = ref('')
  const webphoneInstanceKey = ref(0)
  const webphoneDialToken = ref(0)
  const webphoneCallStatus = ref<'idle' | 'calling' | 'incoming' | 'connected' | 'held'>('idle')
  const webphoneAvailable = ref(true)
  const incomingCallActionToken = ref(0)
  const incomingCallActionType = ref<'accept' | 'reject' | null>(null)
  const incomingCallActionNotificationId = ref('')

  const loading = ref(false)
  const errorMessage = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value)
  const isWebphoneInCall = computed(() =>
    ['calling', 'incoming', 'connected', 'held'].includes(webphoneCallStatus.value),
  )

  const setAuthData = (newToken: string) => {
    setSipWebSocketsBlocked(false)
    token.value = newToken
    localStorage.setItem('tokenHub', newToken)
  }

  const clearAuthData = () => {
    token.value = null
    extension.value = null
    webphoneDomain.value = resolveWebphoneDomain(null)
    localStorage.removeItem('tokenHub')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('extension')
  }

  const resetWebphoneConnection = () => {
    setSipWebSocketsBlocked(true)
    showWebphone.value = false
    webphoneTarget.value = ''
    webphoneRemoteNumber.value = ''
    webphoneCallStatus.value = 'idle'
    webphoneAvailable.value = false
    incomingCallActionType.value = null
    incomingCallActionNotificationId.value = ''
    incomingCallActionToken.value += 1
    webphoneDialToken.value += 1
    webphoneInstanceKey.value += 1
    workspaceStore.setCurrentNumber('')
    closeTrackedSipWebSockets()
  }

  const setExtensionData = (newExtension: any | null) => {
    const previousNumber = extension.value?.number || ''
    const previousPassword = extension.value?.key || ''
    const previousDomain = webphoneDomain.value

    extension.value = newExtension
    if (newExtension) {
      localStorage.setItem('extension', JSON.stringify(newExtension))
    } else {
      localStorage.removeItem('extension')
    }

    const nextDomain = resolveWebphoneDomain(newExtension)
    webphoneDomain.value = nextDomain
    if (newExtension) {
      webphoneAvailable.value = true
    }

    const nextNumber = extension.value?.number || ''
    const nextPassword = extension.value?.key || ''
    const credentialsChanged =
      previousNumber !== nextNumber ||
      previousPassword !== nextPassword ||
      previousDomain !== nextDomain

    if (showWebphone.value && credentialsChanged && previousNumber) {
      webphoneInstanceKey.value += 1
    }
  }

  const login = async (username: string, password: string) => {
    try {
      loading.value = true
      errorMessage.value = null

      const response = await authService.login({ username, password })
      setAuthData(response.access_token)

      const extensionData = await authService.me()
      setExtensionData(extensionData as any)
      showWebphone.value = true

      toast.add({
        severity: 'success',
        summary: t('common.auth.successful_login'),
        life: 3000,
      })

      router.push({ name: 'home' })
    } catch (error) {
      const isExtensionNotFound =
        isAxiosError(error) && error.response?.data?.detail === EXTENSION_NOT_FOUND_DETAIL
      const detail = isExtensionNotFound
        ? t('common.auth.extension_disabled_admin')
        : t('common.auth.incorrect_email_password')

      if (isExtensionNotFound) {
        clearAuthData()
        errorMessage.value = detail
      } else {
        errorMessage.value = t('common.auth.incorrect_credentials')
      }

      toast.add({
        severity: 'error',
        summary: t('common.auth.login_error'),
        detail,
        life: 3000,
      })
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      resetWebphoneConnection()
    } catch {
    } finally {
      clearAuthData()

      toast.add({
        severity: 'info',
        summary: t('common.auth.sesion_closed'),
        life: 3000,
      })

      router.push('/auth/sign-in')
    }
  }

  const setWebphoneTarget = (number: string | number | null | undefined) => {
    if (number === null || number === undefined) {
      webphoneTarget.value = ''
      workspaceStore.setCurrentNumber('')
      return
    }

    webphoneTarget.value = String(number).trim()
    workspaceStore.setCurrentNumber(webphoneTarget.value)
  }

  const setWebphoneRemoteNumber = (number: string | number | null | undefined) => {
    if (number === null || number === undefined) {
      webphoneRemoteNumber.value = ''
      return
    }

    webphoneRemoteNumber.value = String(number).trim()
    if (webphoneRemoteNumber.value) {
      workspaceStore.setCurrentNumber(webphoneRemoteNumber.value)
    }
  }

  const openWebphone = (number?: string | number | null) => {
    if (!hasRequiredPermissions.value) {
      return false
    }

    if (!webphoneAvailable.value) {
      toast.add({
        severity: 'warn',
        summary: 'Extensión no disponible',
        detail: 'No puedes realizar llamadas mientras la extensión esté desconectada.',
        life: 3000,
      })
      return false
    }

    const normalizedRequestedNumber =
      number === null || number === undefined ? '' : String(number).trim()
    setWebphoneTarget(number)
    showWebphone.value = true
    if (!isWebphoneInCall.value && normalizedRequestedNumber) {
      webphoneDialToken.value += 1
    }
    return true
  }

  const closeWebphone = () => {
    if (isWebphoneInCall.value) {
      toast.add({
        severity: 'warn',
        summary: 'No puedes cerrar el webphone durante una llamada',
        life: 3000,
      })
      return
    }
    showWebphone.value = false
  }

  const toggleWebphone = () => {
    if (showWebphone.value && isWebphoneInCall.value) {
      toast.add({
        severity: 'warn',
        summary: 'No puedes cerrar el webphone durante una llamada',
        life: 3000,
      })
      return
    }

    if (!showWebphone.value) {
      if (!hasRequiredPermissions.value) {
        return
      }
    }
    showWebphone.value = !showWebphone.value
  }

  const setWebphoneCallStatus = (
    status: 'idle' | 'calling' | 'incoming' | 'connected' | 'held',
  ) => {
    webphoneCallStatus.value = status
  }

  const setWebphoneAvailability = (available: boolean) => {
    webphoneAvailable.value = available
  }

  const requestIncomingCallAction = (action: 'accept' | 'reject', notificationId?: string) => {
    incomingCallActionType.value = action
    incomingCallActionNotificationId.value = (notificationId ?? '').trim()
    incomingCallActionToken.value += 1
  }

  return {
    token,
    extension,
    loading,
    errorMessage,
    isAuthenticated,
    login,
    logout,
    setAuthData,
    setWebphoneTarget,
    resetWebphoneConnection,
    openWebphone,
    closeWebphone,
    toggleWebphone,
    showWebphone,
    webphoneTarget,
    webphoneRemoteNumber,
    webphoneInstanceKey,
    webphoneDialToken,
    webphoneCallStatus,
    webphoneAvailable,
    incomingCallActionToken,
    incomingCallActionType,
    incomingCallActionNotificationId,
    isWebphoneInCall,
    webphoneNumber,
    webphonePassword,
    webphoneDomain,
    setWebphoneCallStatus,
    setWebphoneAvailability,
    setWebphoneRemoteNumber,
    requestIncomingCallAction,
  }
})
